import React, { useContext, useEffect } from 'react';
import { connect } from 'react-redux';
import { Divider, Card, Menu, Typography, message, Modal, Drawer } from 'antd';
import {
  ApplicationUIContext,
  ButtonGroup,
  useDrawerState,
  Form,
  helpers,
} from '@yuhuinc/ui-library';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { StyledHeader } from '../common/Styles';
import { getTranslatedString } from 'helpers/i18n';
import {
  canApproveApplication,
  canDenyApplication,
  canCancelApplication,
  canReviewApplication,
  canCreateLeaseForApplication,
  canRequestAdditionalDetailsForApplication,
  canRequestChangesForApplication,
} from './../common/helpers';
import { apiPost } from 'helpers/ApiHelper';
import { IncomeVerification } from './review/IncomeVerification';
import { CurrentApplicationContext } from '../ApplicationOverview';
import CancelApplicationForm from './overview/CancelApplicationForm';
import { phaseTwoWip } from '../common/actions/WipModal';
import CreateLeaseButton from '../common/actions/CreateLeaseButton';

import {
  missingContactsTitle,
  missingContactsText,
  applicantsWithoutContactMethod,
} from '../common/MissingContactMethods';
import { resetApplicationStatuses } from '../store/actions';

import Stats from './review/Stats';
import ReviewLog from './review/ReviewLog';
import BackgroundChecks from './review/BackgroundChecks';
import Flags from './review/Flags';
import Documents from './review/Documents';
import References from './review/References';
import ReviewDrawer from './review/ReviewDrawer';
import RequestAdditionalDetailsDrawer from './review/RequestAdditionalDetailsDrawer';
import RequestChangesDrawer from './review/RequestChangesDrawer';
import DenyApplicationForm from './review/DenyApplicationForm';
import { featureEnabled } from 'helpers/ToggleBot';

const { WIDE_CARD_WIDTH } = helpers;

const { Title } = Typography;

const approveText = getTranslatedString(
  'landlordPortal.applicationManagement.applicationOverview.review.actions.approve'
);

const denyText = getTranslatedString(
  'landlordPortal.applicationManagement.applicationOverview.review.actions.deny'
);

const cancelApplicationText = getTranslatedString(
  'landlordPortal.applicationManagement.applicationOverview.review.actions.cancelApplication'
);

const requestAdditionalDetailsText = getTranslatedString(
  'landlordPortal.applicationManagement.applicationOverview.review.actions.requestAdditionalDetails'
);

const requestChangesText = getTranslatedString(
  'landlordPortal.applicationManagement.applicationOverview.review.actions.requestChanges'
);

const approvalSuccessText = getTranslatedString(
  'landlordPortal.applicationManagement.applicationOverview.review.actions.approve.success'
);

const denySuccessText = getTranslatedString(
  'landlordPortal.applicationManagement.applicationOverview.review.actions.deny.applicationDeclined'
);

const errorSuccessText = getTranslatedString(
  'landlordPortal.applicationManagement.applicationOverview.review.actions.approve.error'
);

const approvalConfirmationTitle = getTranslatedString(
  'landlordPortal.applicationManagement.applicationOverview.review.actions.approve.confirmationModal.title'
);

const approvalConfirmationContent = getTranslatedString(
  'landlordPortal.applicationManagement.applicationOverview.review.actions.approve.confirmationModal.content'
);

const Review = ({ resetApplicationStatuses }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const application = useContext(CurrentApplicationContext);
  const [denyApplicationForm] = Form.useForm();
  const denyReason = Form.useWatch('deny_reason', denyApplicationForm);
  const [denyModal, setDenyModal] = React.useState({});
  const [, reviewApplicationDrawer] = useDrawerState(false);
  const [, cancelApplicationDrawer] = useDrawerState(false);
  const [, requestAdditionalDetailsDrawer] = useDrawerState(false);
  const [, requestChangesDrawer] = useDrawerState(false);
  const queryClient = useQueryClient();

  const canApprove = canApproveApplication(application);
  const canDeny = canDenyApplication(application);
  const canCancel = canCancelApplication(application);
  const canReview = canReviewApplication(application);
  const canCreateLease = canCreateLeaseForApplication(application);
  const canRequestAdditionalDetails = canRequestAdditionalDetailsForApplication(application);
  const canRequestChanges = canRequestChangesForApplication(application);

  const approveApplicationMutation = useMutation({
    mutationFn: (data) =>
      apiPost(`/landlords/application_management/applications/${data.id}/approve`),
    onSuccess: () => {
      message.success(approvalSuccessText).then(resetApplicationStatuses());
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
    onError: () => {
      message.error(errorSuccessText);
    },
  });

  const denyApplicationMutation = useMutation({
    mutationFn: (data) =>
      apiPost(`/landlords/application_management/applications/${data.id}/deny`, data),
    onSuccess: () => {
      message.success(denySuccessText).then(resetApplicationStatuses());
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
    onError: () => message.error(getTranslatedString('unknownError')),
  });

  const cancelApplicationMutation = useMutation({
    mutationFn: (data) => {
      return apiPost(`/landlords/application_management/applications/${application.id}/cancel`, {
        cancellation_reason: 'Unit unavailable',
      });
    },
    onSuccess() {
      message.success(
        getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.CancelApplication.drawer.success'
        )
      );
    },
    onError() {
      // TODO: Ask Product for a proper error message
      message.error('Error at cancelling');
    },
    onSettled() {
      queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
  });

  useEffect(() => {
    if (denyModal && denyModal.update) {
      denyModal.update({ okButtonProps: { disabled: typeof denyReason === 'undefined' } });
    }
  }, [denyReason]);

  const onSubmitDenyApplication = (values) => {
    denyApplicationMutation.mutate({ id: application.id, ...values });
  };

  const handleApproveButtonOnClick = () => {
    const increaseButtonSizeOnMobile = { size: isMobile ? 'large' : 'default' };

    const approveModal = () => {
      return Modal.confirm({
        title: approvalConfirmationTitle,
        onOk: () => approveApplicationMutation.mutate({ id: application.id }),
        okText: approveText,
        okCancel: true,
        cancelText: getTranslatedString('cancel'),
        content: approvalConfirmationContent,
        okButtonProps: increaseButtonSizeOnMobile,
        cancelButtonProps: increaseButtonSizeOnMobile,
        bodyStyle: { fontSize: isMobile ? 16 : 14 },
        destroyOnClose: true,
      });
    };

    if (applicantsWithoutContactMethod(application).length === 0) {
      approveModal();
    } else {
      const confirmationModal = Modal.confirm({
        title: missingContactsTitle,
        content: missingContactsText(application),
        onOk: async () => {
          confirmationModal.destroy();
          approveModal();
        },
      });
    }
  };

  const handleDenyButtonOnClick = () => {
    const increaseButtonSizeOnMobile = { size: isMobile ? 'large' : 'default' };

    const denyModal = () => {
      return setDenyModal(
        Modal.confirm({
          title: getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.review.actions.deny.declineApplication'
          ),
          content: (
            <DenyApplicationForm
              denialReasons={application.application_denial_reasons}
              form={denyApplicationForm}
              onSubmit={onSubmitDenyApplication}
            />
          ),
          onOk: () => {
            onSubmitDenyApplication(denyApplicationForm.getFieldsValue());
          },
          okText: getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.review.actions.deny.decline'
          ),
          cancelText: getTranslatedString('cancel'),
          okButtonProps: { ...increaseButtonSizeOnMobile, disabled: true },
          cancelButtonProps: increaseButtonSizeOnMobile,
          bodyStyle: { fontSize: isMobile ? 16 : 14 },
          destroyOnClose: true,
        })
      );
    };

    if (applicantsWithoutContactMethod(application).length === 0) {
      denyModal();
    } else {
      const confirmationModal = Modal.confirm({
        title: missingContactsTitle,
        content: missingContactsText(application),
        onOk: async () => {
          confirmationModal.destroy();
          denyModal();
        },
      });
    }
  };

  const handleCancelButtonOnClick = () => {
    if (application?.lifecycle?.substatus === 'unit_unavailable') {
      cancelApplicationMutation.mutate();
    } else {
      cancelApplicationDrawer.open();
    }
  };

  // TODO: Add link on "Overrides" value in the Flags Section
  // TODO: Once available, fetch stats from API and show them here

  const secondaryActionButtons = [
    application.can_approve &&
      canRequestAdditionalDetails && {
        key: 'request_additional_applicant_details',
        label: requestAdditionalDetailsText,
        onClick: () => phaseTwoWip(requestAdditionalDetailsDrawer.open),
      },
    canRequestChanges && {
      key: 'request_changes',
      label: requestChangesText,
      onClick: () => phaseTwoWip(requestChangesDrawer.open),
    },
  ].filter(Boolean);

  const isRequestAdditionalDetailsButtonInSecondaryActionButtons = secondaryActionButtons.some(
    (button) => button.key === 'request_additional_applicant_details'
  );

  const mainActionButtons = [
    canReview && {
      label: 'Review Application',
      type: 'primary',
      onClick: () => reviewApplicationDrawer.open(),
    },
    application.can_review &&
      canRequestAdditionalDetails &&
      !isRequestAdditionalDetailsButtonInSecondaryActionButtons && {
        key: 'request_additional_applicant_details',
        label: requestAdditionalDetailsText,
        onClick: () => phaseTwoWip(requestAdditionalDetailsDrawer.open),
      },
    canApprove && {
      label: approveText,
      key: 'approve_application',
      onClick: handleApproveButtonOnClick,
    },
    canDeny && {
      label: denyText,
      key: 'deny_application',
      type: 'secondary',
      onClick: handleDenyButtonOnClick,
    },
    canCreateLease && <CreateLeaseButton key="create_lease" />,
    canCancel && {
      label: cancelApplicationText,
      key: 'cancel_application',
      type: 'secondary',
      onClick: handleCancelButtonOnClick,
      loading: cancelApplicationMutation.isLoading,
    },
  ].filter(Boolean);

  return (
    <Card style={{ maxWidth: WIDE_CARD_WIDTH }}>
      <StyledHeader>
        <Title level={isMobile ? 3 : 4}>
          {getTranslatedString(
            'landlordPortal.applicationManagement.applicationOverview.review.cardTitle'
          )}
        </Title>
        <ButtonGroup
          buttons={mainActionButtons}
          dropdownOverlay={
            secondaryActionButtons.length > 0 && <Menu items={secondaryActionButtons} />
          }
        />
        <ReviewDrawer
          isVisible={reviewApplicationDrawer.isVisible}
          onClose={reviewApplicationDrawer.close}
        />
      </StyledHeader>

      <Divider />

      <Stats />

      <Divider />

      <ReviewLog />

      <Divider />

      {!featureEnabled('4-0-phase-2-wip') && <IncomeVerification />}

      <Divider />

      <BackgroundChecks />

      <Divider />

      {!featureEnabled('4-0-phase-2-wip') && <Flags />}

      <Divider />

      <Documents />

      {!featureEnabled('4-0-phase-2-wip') && <References />}

      <Drawer
        title={getTranslatedString(
          'landlordPortal.applicationManagement.applicationOverview.CancelApplication.drawer.title'
        )}
        visible={cancelApplicationDrawer.isVisible}
        closable
        destroyOnClose
        onClose={cancelApplicationDrawer.close}
      >
        <CancelApplicationForm afterSubmit={cancelApplicationDrawer.close} />
      </Drawer>
      <RequestAdditionalDetailsDrawer drawerState={requestAdditionalDetailsDrawer} />
      <RequestChangesDrawer drawerState={requestChangesDrawer} />
    </Card>
  );
};

const mapStoreToProps = (store) => ({
  currentUser: store.userTypes,
});

const mapDispatchToProps = {
  resetApplicationStatuses,
};

export default connect(mapStoreToProps, mapDispatchToProps)(Review);
